import {
  Flex,
  TooltipTrigger,
  Button,
  TooltipContent,
  TabBar,
  Tooltip,
  Status,
} from "@repo/ping-design";
import { useState, useCallback } from "react";
import {
  MISSING_VALUE,
  type LatLngType,
  type BuildingPanelSection,
  getNriScaleEntryForLabel,
} from "@repo/ping-react-js";
import "./TopSection.scss";

const INSET_MAP_ENABLED = true;

export const TopSection = ({
  latLng,
  section,
}: {
  latLng: LatLngType;
  section: BuildingPanelSection;
}) => {
  const [mapType, setMapType] = useState<string>("satellite");
  const [zoom, setZoom] = useState<number>(18);

  const renderField = useCallback(
    (field: any) => {
      const displayValue = field?.v ?? MISSING_VALUE;

      if (section.type === "MAPS") {
        return (
          <Flex
            key={field.k}
            direction="row"
            gap="3"
            align="center"
            justify="space-between"
            className="TopSection__ValueSection__Data__Maps"
          >
            <span className="TopSection__ValueSection__Data__Maps__Label">
              {field.k}
            </span>
            <span className="TopSection__ValueSection__Data__Maps__Value">
              {displayValue}
            </span>
          </Flex>
        );
      } else if (section.type === "PLI") {
        const color = getNriScaleEntryForLabel(field?.v)?.conversion.color;

        return (
          <Flex
            key={field.k}
            direction="row"
            align="center"
            justify="space-between"
            className="TopSection__ValueSection__Data__Pli"
          >
            <span className="TopSection__ValueSection__Data__Pli__Label">
              {field.k}
            </span>
            <Status
              label={String(displayValue)}
              color={color}
              className="TopSection__ValueSection__Data__Pli__Status"
            />
          </Flex>
        );
      }
    },
    [section.type],
  );

  return (
    <Flex
      className="TopSection"
      direction="row"
      gap="8"
      justify="space-between"
    >
      <Flex direction="column" className="TopSection__ValueSection" gap="2">
        <div className="TopSection__ValueSection__Data">
          {section?.values?.map((field: any) => {
            return renderField(field);
          })}
        </div>
      </Flex>
      {section.type !== "PLI" && INSET_MAP_ENABLED && (
        <Flex className="TopSection__MapSection" direction="column" gap="2">
          {isNaN(latLng?.lat) || isNaN(latLng?.lng) ? (
            <div className="TopSection__MapSection__Image">
              <div className="TopSection__MapSection__Image--NotAvailable">
                <span>Map not available</span>
              </div>
            </div>
          ) : (
            <img
              src={
                isNaN(latLng?.lat) || isNaN(latLng?.lng)
                  ? undefined
                  : `http://maps.google.com/maps/api/staticmap?zoom=${zoom}&size=350x350&maptype=${mapType}&markers=${latLng?.lat},${latLng?.lng}&sensor=false&key=${import.meta.env.VITE_APP_PING_GOOGLE_API_KEY}`
              }
              className="TopSection__MapSection__Image"
            />
          )}
          <Flex direction="row" justify="space-between">
            <Flex direction="row" gap="1">
              <Tooltip placement="bottom">
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    iconName="Plus"
                    size="sm"
                    onClick={() => {
                      setZoom((prev) => prev + 1);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>Zoom In</TooltipContent>
              </Tooltip>
              <Tooltip placement="bottom">
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    iconName="Minus"
                    size="sm"
                    onClick={() => {
                      setZoom((prev) => prev - 1);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent>Zoom Out</TooltipContent>
              </Tooltip>
            </Flex>
            <TabBar
              variant="sm"
              selected={mapType}
              className="TopSection__MapSection__TabBar"
              options={[
                {
                  id: "satellite",
                  label: "Satellite",
                },
                {
                  id: "roadmap",
                  label: "Map",
                },
              ]}
              onChange={(id) => {
                setMapType(id);
              }}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
